import { useRequiredContext } from "@redotech/react-util/context";
import BoxIcon from "@redotech/redo-icons/box-01.svg";
import BoxIconFilled from "@redotech/redo-icons/box_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  OutboundViewCountsContext,
  OutboundViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { OutboundSystemView } from "@redotech/redo-model/views/default-outbound-views";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo, useState } from "react";
import {
  BATCH_HISTORY_ICON,
  BATCH_HISTORY_ICON_FILLED,
  EOD_MANIFEST_ICON,
  EOD_MANIFEST_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
  OUTBOUND_ICON,
  OUTBOUND_ICON_FILLED,
  PICK_SESSIONS_ICON,
  PICK_SESSIONS_ICON_FILLED,
  PRINT_JOBS_ICON,
  PRINT_JOBS_ICON_FILLED,
  RATE_CALCULATOR_ICON,
  RATE_CALCULATOR_ICON_FILLED,
  SCAN_ICON,
  SCAN_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { FulfillmentItem } from "./fulfillment";
import { PickupsItem } from "./pickups";
import { useIsChildRouteActive } from "./use-child-route-active";

/**
 * Routes that appear at the Operations level (outside the Outbound section).
 * These should NOT cause the Outbound section to auto-expand.
 */
const OPERATIONS_LEVEL_ROUTES = ["dashboard", "orders", "about"];

export const OutboundSection = memo(function OutboundSection() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled, wmsEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  const canViewScan = useHasPermission(
    OrderManagementPermissions.SCAN_AND_VERIFY,
  );
  const canManagePickSessions = useHasPermission(
    OrderManagementPermissions.MANAGE_PICK_SESSIONS,
  );

  const team = useRequiredContext(TeamContext);
  const overboxLabelsEnabled =
    !!team.settings.outboundLabels?.useApcCarrierAccount ||
    !!team.settings.outboundLabels?.useEpostGlobalCarrierAccount;

  const outboundViewsContext = useRequiredContext(OutboundViewsContext);
  const outboundViewCountsContext = useContext(OutboundViewCountsContext);

  const awaitingShipmentCount = useMemo(() => {
    if (!outboundViewCountsContext?.viewCounts) {
      return undefined;
    }
    const awaitingShipmentView = outboundViewsContext.views.find(
      (view) => view.name === OutboundSystemView.AWAITING_SHIPMENT,
    );
    if (!awaitingShipmentView) {
      return undefined;
    }
    const count =
      outboundViewCountsContext.viewCounts[awaitingShipmentView._id] || 0;
    return count > 0 ? count : undefined;
  }, [outboundViewsContext.views, outboundViewCountsContext?.viewCounts]);

  // Auto-expand when on any child route of Outbound
  const excludedRoutes = useMemo(() => OPERATIONS_LEVEL_ROUTES, []);
  const isAnyChildActive = useIsChildRouteActive(
    routes.ordersOutboundLabelsHref,
    excludedRoutes,
  );

  const [outboundExpanded, setOutboundExpanded] = useState(false);

  // Don't show Outbound section if OMS is not enabled
  if (!omsEnabled) {
    return null;
  }

  // Don't show if user has no permissions for any outbound items
  if (!canViewShippingTabs && !canViewScan) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => setOutboundExpanded((prev) => !prev),
      }}
      anyChildSelected={isAnyChildActive}
      FilledIcon={OUTBOUND_ICON_FILLED}
      friendlyName="Outbound"
      id="outbound"
      notificationCount={awaitingShipmentCount}
      UnfilledIcon={OUTBOUND_ICON}
    >
      <FulfillmentItem autoExpand={outboundExpanded} />

      {/* Alphabetized items */}
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("batch-history"),
        }}
        FilledIcon={BATCH_HISTORY_ICON_FILLED}
        friendlyName="Batch history"
        hidden={!canViewShippingTabs}
        id="outbound-batch-history"
        UnfilledIcon={BATCH_HISTORY_ICON}
      />

      <PickupsItem />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("eod-manifest"),
        }}
        FilledIcon={EOD_MANIFEST_ICON_FILLED}
        friendlyName="EOD manifest"
        hidden={!canViewShippingTabs}
        id="outbound-eod-manifest"
        UnfilledIcon={EOD_MANIFEST_ICON}
      />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("overbox-labels"),
        }}
        FilledIcon={BoxIconFilled}
        friendlyName="Overbox labels"
        hidden={!canViewShippingTabs || !overboxLabelsEnabled}
        id="outbound-overbox-labels"
        UnfilledIcon={BoxIcon}
      />

      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.pickSessionTableHref }}
        FilledIcon={PICK_SESSIONS_ICON_FILLED}
        friendlyName="Pick sessions"
        hidden={!wmsEnabled || !canManagePickSessions}
        id="outbound-pick-sessions"
        UnfilledIcon={PICK_SESSIONS_ICON}
      />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("print-jobs"),
        }}
        FilledIcon={PRINT_JOBS_ICON_FILLED}
        friendlyName="Print jobs"
        hidden={!canViewShippingTabs}
        id="outbound-print-jobs"
        UnfilledIcon={PRINT_JOBS_ICON}
      />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("rate-calculator"),
        }}
        FilledIcon={RATE_CALCULATOR_ICON_FILLED}
        friendlyName="Rate calculator"
        hidden={!canViewShippingTabs}
        id="outbound-rate-calculator"
        UnfilledIcon={RATE_CALCULATOR_ICON}
      />

      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("scan"),
        }}
        FilledIcon={SCAN_ICON_FILLED}
        friendlyName="Scan"
        hidden={!canViewScan}
        id="outbound-scan"
        UnfilledIcon={SCAN_ICON}
      />
    </RedoSecondaryNavItem>
  );
});
